import { Box, FormControlLabel, Paper, styled, Switch } from '@mui/material';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { Permissions, ProjectStatuses, ProjectSummary } from '../api/dtos';
import GridActions from '../components/GridActions';
import SetLinks from '../components/layout/SetLinks';
import { StyledButton } from '../components/StyledButton';
import CollapsibleAppTable from '../components/tables/CollapsibleAppTable';
import type { AppColumn } from '../components/tables/AppTable.types';
import { StyledLink } from '../components/utility/StyledLink';
import PermissionsContext from '../contexts/auth/PermissionsContext';
import ProjectsContext from '../contexts/data/ProjectsContext';
import { DefaultProjectCreateLink } from '../contexts/layouts/LinkContext';
import ProjectSummariesContext from '../contexts/ProjectSummariesContext';
import useProjectSummaries from '../hooks/data/useLotStatusCountsByProjectId';

const StyledPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
}));

const EMPTY_PROJECT_SUMMARIES: readonly ProjectSummary[] = [];

function requiredProjectId(row: ProjectSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error('Project summary is missing its project ID.');
    }

    return row.id;
}

export function createProjectColumns(permissions?: readonly Permissions[]): AppColumn<ProjectSummary>[] {
    const columns: AppColumn<ProjectSummary>[] = [
        {
            id: 'name',
            label: 'Project Name',
            scalarType: 'text',
            accessor: (row) => row.name,
            minWidth: 125,
            flex: 1,
            cell: ({ row, formatted }) => (
                <StyledLink to={`/projects/${requiredProjectId(row)}`}>{formatted}</StyledLink>
            ),
        },
        {
            id: 'future',
            label: 'Future Lots',
            scalarType: 'number',
            accessor: (row) => row.future,
            align: 'left',
            width: 110,
            aggregate: 'sum',
        },
        {
            id: 'unavailable',
            label: 'Unavailable',
            scalarType: 'number',
            accessor: (row) => row.unavailable,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
        {
            id: 'comingSoon',
            label: 'Coming Soon',
            scalarType: 'number',
            accessor: (row) => row.comingSoon,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
        {
            id: 'onHold',
            label: 'On Hold',
            scalarType: 'number',
            accessor: (row) => row.onHold,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
        {
            id: 'available',
            label: 'Available',
            scalarType: 'number',
            accessor: (row) => row.available,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
        {
            id: 'salesAdviceIssued',
            label: 'Sales Advice Issued',
            scalarType: 'number',
            accessor: (row) => row.salesAdviceIssued,
            align: 'left',
            width: 160,
            aggregate: 'sum',
        },
        {
            id: 'contractIssued',
            label: 'Contract Issued',
            scalarType: 'number',
            accessor: (row) => row.contractIssued,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
        {
            id: 'contractExchanged',
            label: 'Contract Exchanged',
            scalarType: 'number',
            accessor: (row) => row.contractExchanged,
            align: 'left',
            width: 150,
            aggregate: 'sum',
        },
        {
            id: 'contractSettled',
            label: 'Contract Settled',
            scalarType: 'number',
            accessor: (row) => row.contractSettled,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
        {
            id: 'totalLots',
            label: 'Total Lots',
            scalarType: 'number',
            accessor: (row) => row.totalLots,
            align: 'left',
            width: 125,
            aggregate: 'sum',
        },
    ];

    if (permissions?.includes(Permissions.ProjectsCanEditProject)) {
        columns.push({
            id: 'actions',
            label: '',
            headerLabel: '',
            kind: 'action',
            scalarType: 'text',
            accessor: (row) => String(row.id ?? ''),
            width: 90,
            cell: ({ row }) => (
                <GridActions
                    editPermission={Permissions.ProjectsCanEditProject}
                    editUrl={`/projects/edit/${requiredProjectId(row)}`}
                />
            ),
        });
    }

    return columns;
}

const PROJECT_EXPORTS = {
    released: {
        exportTitle: 'Released To Market Projects',
        fileName: 'Released To Market Projects',
        includeDateTime: true,
    },
    pipeline: {
        exportTitle: 'Pipeline Projects',
        fileName: 'Pipeline Projects',
        includeDateTime: true,
    },
    archived: {
        exportTitle: 'Archived Projects',
        fileName: 'Archived Projects',
        includeDateTime: true,
    },
} as const;

export const Projects: FC = () => {
    const { data: permissions } = useContext(PermissionsContext);
    const navigate = useNavigate();
    const { data: projects } = useContext(ProjectsContext);
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        if (permissions?.includes(Permissions.ProjectsCanSeeArchivedStages)) {
            setShowArchived(true);
        }
    }, [permissions]);

    const context = useProjectSummaries(showArchived);
    const { data: projectSummaries, loading: projectsLoading } = context;
    const summaries = projectSummaries ?? EMPTY_PROJECT_SUMMARIES;
    const releasedToMarket = useMemo(
        () => summaries.filter((row) => row.status === ProjectStatuses.ReleasedToMarket),
        [summaries],
    );
    const pipeline = useMemo(() => summaries.filter((row) => row.status === ProjectStatuses.Pipeline), [summaries]);
    const archived = useMemo(() => summaries.filter((row) => row.status === ProjectStatuses.Archived), [summaries]);

    if (!permissions?.includes(Permissions.ProjectsCanViewPage)) {
        if (projects && projects.length > 0) {
            navigate(`projects/${projects[0]?.id}`);
        } else {
            navigate('error');
        }
    }

    const projectColumns = useMemo(() => createProjectColumns(permissions), [permissions]);

    const sharedTableProps = {
        density: 'compact' as const,
        entityName: 'Projects',
        columns: projectColumns,
        getRowId: requiredProjectId,
        columnControls: {
            menu: true,
        },
        filterControls: true,
        loading: projectsLoading,
    };

    return (
        <ProjectSummariesContext value={context}>
            <Outlet />
            <SetLinks createProjectLink={DefaultProjectCreateLink} stageLinkEnabled={false} lotLinkEnabled={false} />
            <StyledBox>
                {permissions?.includes(Permissions.ProjectsCanSeeArchivedStages) && (
                    <FormControlLabel
                        label="Show Archived Stages"
                        control={
                            <Switch
                                checked={showArchived}
                                onChange={(toggle) => setShowArchived(toggle.target.checked)}
                            />
                        }
                    />
                )}
                {permissions?.includes(Permissions.ProjectsCanAddProject) && (
                    <StyledButton
                        variant="contained"
                        //@ts-expect-error
                        component={Link}
                        to="/projects/create"
                        state={{
                            back: true,
                        }}
                    >
                        Add Project
                    </StyledButton>
                )}
            </StyledBox>
            <StyledPaper>
                <CollapsibleAppTable
                    {...sharedTableProps}
                    title="Released To Market"
                    pluralizeTitle={false}
                    rows={releasedToMarket}
                    export={PROJECT_EXPORTS.released}
                />
            </StyledPaper>
            <StyledPaper>
                <CollapsibleAppTable
                    {...sharedTableProps}
                    title="Pipeline"
                    pluralizeTitle={false}
                    startCollapsed
                    rows={pipeline}
                    export={PROJECT_EXPORTS.pipeline}
                />
            </StyledPaper>
            <StyledPaper>
                <CollapsibleAppTable
                    {...sharedTableProps}
                    title="Archived"
                    pluralizeTitle={false}
                    startCollapsed
                    rows={archived}
                    export={PROJECT_EXPORTS.archived}
                />
            </StyledPaper>
        </ProjectSummariesContext>
    );
};
